import { supabase } from '../lib/supabase';

export const knowledgeHubService = {
  // Get all content with optional filters
  async getAllContent(filters = {}) {
    let query = supabase?.from('knowledge_hub_content')?.select('*')?.order('publish_date', { ascending: false });

    if (filters?.contentType && filters?.contentType !== 'all') {
      query = query?.eq('content_type', filters?.contentType);
    }

    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.searchTerm) {
      query = query?.or(`title.ilike.%${filters?.searchTerm}%,excerpt.ilike.%${filters?.searchTerm}%,content.ilike.%${filters?.searchTerm}%`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Get published content only (for public view)
  async getPublishedContent(contentType = null) {
    let query = supabase?.from('knowledge_hub_content')?.select('*')?.eq('status', 'published')?.order('publish_date', { ascending: false });

    if (contentType && contentType !== 'all') {
      query = query?.eq('content_type', contentType);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Get single content by ID
  async getContentById(id) {
    const { data, error } = await supabase?.from('knowledge_hub_content')?.select('*')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  },

  // Get single content by slug
  async getContentBySlug(slug) {
    const { data, error } = await supabase?.from('knowledge_hub_content')?.select('*')?.eq('slug', slug)?.single();

    if (error) throw error;
    return data;
  },

  // Create new content
  async createContent(contentData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase?.from('knowledge_hub_content')?.insert([{
        ...contentData,
        created_by: user?.id
      }])?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Update existing content
  async updateContent(id, updates) {
    const { data, error } = await supabase?.from('knowledge_hub_content')?.update(updates)?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Delete content
  async deleteContent(id) {
    const { error } = await supabase?.from('knowledge_hub_content')?.delete()?.eq('id', id);

    if (error) throw error;
  },

  // Bulk delete content
  async bulkDeleteContent(ids) {
    const { error } = await supabase?.from('knowledge_hub_content')?.delete()?.in('id', ids);

    if (error) throw error;
  },

  // Increment view count
  async incrementViewCount(id) {
    const { error } = await supabase?.rpc('increment_view_count', { content_id: id });
    if (error) {
      // Fallback if RPC doesn't exist
      const { data: content } = await supabase?.from('knowledge_hub_content')?.select('view_count')?.eq('id', id)?.single();
      
      if (content) {
        await supabase?.from('knowledge_hub_content')?.update({ view_count: (content?.view_count || 0) + 1 })?.eq('id', id);
      }
    }
  },

  // Generate slug from title
  generateSlug(title) {
    return title
      ?.toLowerCase()
      ?.replace(/[^a-z0-9]+/g, '-')?.replace(/^-+|-+$/g, '');
  },

  // Save a version snapshot before updating an article
  async saveArticleVersion(articleId, snapshot, statusAtSave) {
    const { data: { user } } = await supabase?.auth?.getUser();

    const { data, error } = await supabase?.from('article_versions')?.insert([{
      article_id: articleId,
      snapshot: snapshot || {},
      status_at_save: statusAtSave || 'draft',
      saved_by: user?.id,
      saved_at: new Date()?.toISOString()
    }])?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Get all versions for an article, newest first
  async getArticleVersions(articleId) {
    const { data, error } = await supabase
      ?.from('article_versions')?.select('*')?.eq('article_id', articleId)?.order('saved_at', { ascending: false });

    if (error) throw error;
    return data;
  }
};